import { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchMedia, uploadMedia, updateMedia, deleteMedia } from '../api';

// Admin "Media Library" manager. Lets admins upload, caption, reorder, and
// delete images in each media category. The landing-page gallery + Future
// Vision slideshow read the same categories, so changes here are reflected
// on the public site (images served directly from Azure Blob Storage).
//
// Follows the app-wide rule: key effects off a stable accountKey string, never
// the raw MSAL account object (which is a fresh reference every render).

const CATEGORIES = [
  { key: 'cabins', label: 'Cabins' },
  { key: 'farm', label: 'Farm' },
  { key: 'experiences', label: 'Experiences' },
  { key: 'future-vision', label: 'Future Vision' },
];

export default function AdminMedia() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;

  const [category, setCategory] = useState('cabins');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetchMedia(category);
      setItems(res.items || []);
    } catch {
      setErr('Could not load this category.');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    accountKey; // keep effect tied to a stable identity key
    load();
  }, [load, accountKey]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setErr('');
    setMsg('');
    let ok = 0;
    for (const file of files) {
      try {
        const r = await uploadMedia(instance, account, category, file, '');
        if (r.ok) ok += 1;
        else setErr(r.data?.message || `Upload failed for ${file.name}.`);
      } catch {
        setErr(`Upload failed for ${file.name}.`);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
    setBusy(false);
    if (ok) setMsg(`Uploaded ${ok} image${ok === 1 ? '' : 's'}.`);
    load();
  };

  const onSaveCaption = async (item, caption) => {
    setBusy(true);
    try {
      await updateMedia(instance, account, category, item.name, { caption });
      setItems((list) => list.map((i) => (i.name === item.name ? { ...i, caption } : i)));
      setMsg('Caption saved.');
    } catch {
      setErr('Could not save caption.');
    } finally {
      setBusy(false);
    }
  };

  const onReorder = async (item, order) => {
    setBusy(true);
    try {
      await updateMedia(instance, account, category, item.name, { order: Number(order) });
      setMsg('Order updated.');
      load();
    } catch {
      setErr('Could not update order.');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this image? This cannot be undone.')) return;
    setBusy(true);
    try {
      await deleteMedia(instance, account, category, item.name);
      setItems((list) => list.filter((i) => i.name !== item.name));
      setMsg('Image deleted.');
    } catch {
      setErr('Could not delete image.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card admin-media">
      <h2>Media Library</h2>
      <p className="subtle">
        Manage the photos shown on the public site. <strong>Cabins</strong>,{' '}
        <strong>Farm</strong>, and <strong>Experiences</strong> feed the Photo Gallery;{' '}
        <strong>Future Vision</strong> feeds the slideshow. Changes appear on the site within a minute.
      </p>

      <div className="media-cat-tabs">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            className={`media-cat-tab${category === c.key ? ' is-active' : ''}`}
            onClick={() => setCategory(c.key)}
            disabled={busy}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="media-upload-row">
        <label className="btn media-upload-btn">
          {busy ? 'Working…' : '+ Upload images'}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
            multiple
            onChange={onUpload}
            disabled={busy}
            hidden
          />
        </label>
        <span className="subtle">JPEG, PNG, WebP, GIF or AVIF · up to 15 MB each</span>
      </div>

      {msg && <p className="media-msg">{msg}</p>}
      {err && <p className="media-err">{err}</p>}
      {loading && <p className="subtle">Loading…</p>}
      {!loading && items.length === 0 && <p className="subtle">No images in this category yet.</p>}

      <div className="media-admin-grid">
        {items.map((item) => (
          <div className="media-admin-card" key={item.name}>
            <img src={item.url} alt={item.caption || item.name} loading="lazy" />
            <label className="media-field">
              <span>Caption</span>
              <input
                type="text"
                defaultValue={item.caption}
                placeholder="Optional caption"
                onBlur={(e) => {
                  if (e.target.value !== item.caption) onSaveCaption(item, e.target.value);
                }}
                disabled={busy}
              />
            </label>
            <div className="media-card-actions">
              <label className="media-order">
                Order
                <input
                  type="number"
                  min="0"
                  defaultValue={item.order ? Number(item.order) : ''}
                  onBlur={(e) => { if (e.target.value !== '') onReorder(item, e.target.value); }}
                  disabled={busy}
                />
              </label>
              <button className="btn-link danger" onClick={() => onDelete(item)} disabled={busy}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
